import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { Card, CardContent } from "./ui/card";
import { ArrowRight, Play, Pause, Volume2, VolumeX, User, X, Sparkles } from "lucide-react";

/**
 * Reel-style vertical-video transformation row.
 *
 * Scrollable horizontally on mobile (TikTok / Reels feel). Each card is
 * 9:16. When a card scrolls into the centre of the viewport its video
 * autoplays (muted). Tap to expand to a full modal with sound and big
 * Problem / Solution / Result content.
 *
 * Fully backwards compatible: a transformation without `video_url`
 * gracefully falls back to the existing Before / After tile.
 */
export default function ReelCarousel({
  title = "Real people, real wellness journeys",
  limit = 8,
}) {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null); // index of expanded card
  const [lang, setLang] = useState("all");
  const trackRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/transformations");
        setItems((data || []).slice(0, limit * 2));
      } catch {
        /* ignore */
      }
    })();
  }, [limit]);

  // Filter by language tag. Items missing `language` are treated as English
  // for backwards-compat; "all" shows everything.
  const filtered = items.filter((t) => {
    if (lang === "all") return true;
    const itemLang = (t.language || "en").toLowerCase();
    return itemLang === lang || itemLang === "all";
  }).slice(0, limit);

  // Show the tabs only when there's a meaningful mix of languages,
  // OR when at least one Hindi item exists — keeps the UI quiet for
  // English-only setups.
  const langCounts = items.reduce((acc, t) => {
    const k = (t.language || "en").toLowerCase();
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const showTabs = (langCounts.hi || 0) > 0;

  if (filtered.length === 0 && items.length === 0) return null;

  return (
    <section data-testid="reel-carousel">
      <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            Real journeys
          </div>
          <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 mt-1 leading-tight">
            {title}
          </h2>
          <p className="text-sm text-emerald-900/60 mt-1">
            Tap a card to watch — no audio until you open it.
          </p>
        </div>
        {showTabs ? (
          <div className="inline-flex rounded-full bg-emerald-50/80 border border-emerald-100 p-1 text-sm" data-testid="reel-lang-tabs">
            {[
              { key: "all", label: "All" },
              { key: "en", label: "English" },
              { key: "hi", label: "हिंदी" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setLang(tab.key)}
                className={`px-4 py-1.5 rounded-full font-medium transition ${
                  lang === tab.key
                    ? "bg-emerald-700 text-white shadow-sm"
                    : "text-emerald-800 hover:bg-white/60"
                }`}
                data-testid={`reel-lang-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/40 p-8 text-center text-emerald-900/70 text-sm" data-testid="reel-empty">
          No journeys in this language yet — check back soon.
        </div>
      ) : (
      <div
        ref={trackRef}
        className="relative -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth flex gap-4 lg:grid lg:grid-cols-4 lg:gap-5 pb-2"
        style={{ scrollbarWidth: "thin" }}
      >
        {filtered.map((t, idx) => (
          <ReelCard
            key={t.id}
            data={t}
            index={idx}
            onOpen={() => {
              track.reelModalOpened(t, idx);
              setActive(idx);
            }}
          />
        ))}
      </div>
      )}

      {/* Expanded modal — full-screen on mobile, centered card on desktop */}
      {active !== null ? (
        <ReelModalFullscreen
          data={filtered[active]}
          onClose={() => setActive(null)}
        />
      ) : null}
    </section>
  );
}

/* ------------------------------ Card ------------------------------ */

function ReelCard({ data, index, onOpen }) {
  const t = data;
  const videoRef = useRef(null);
  const wrapRef = useRef(null);

  // Autoplay when in viewport centre, pause otherwise.
  // Also fire `reel_impression` once the card is meaningfully visible —
  // deduped per-session inside `track.reelImpression` so PostHog doesn't
  // get spammed as cards autoplay in/out of view.
  useEffect(() => {
    const v = videoRef.current;
    const el = wrapRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.5) {
            if (v && t.video_url) v.play().catch(() => {});
            track.reelImpression(t, index);
          } else {
            if (v) v.pause();
          }
        });
      },
      { threshold: [0, 0.5, 0.9] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [t, index]);

  const hasVideo = Boolean(t.video_url);

  return (
    <button
      ref={wrapRef}
      type="button"
      onClick={onOpen}
      className="group relative shrink-0 w-[78vw] max-w-[300px] lg:w-full lg:max-w-none aspect-[9/16] rounded-2xl overflow-hidden bg-emerald-100 snap-center text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
      data-testid={`reel-card-${t.id}`}
      aria-label={`Play story from ${t.name}`}
    >
      {/* Media: video, poster fallback, or initial-letter placeholder */}
      {hasVideo ? (
        <video
          ref={videoRef}
          src={t.video_url}
          poster={t.video_poster_url || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : t.video_poster_url || t.after_image_url ? (
        <img
          src={t.video_poster_url || t.after_image_url}
          alt={t.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <PlaceholderTile initial={t.name?.[0] || "?"} tone="emerald" />
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />

      {/* Top — condition badge */}
      {t.condition ? (
        <div className="absolute top-3 left-3 max-w-[80%]">
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold bg-white/90 text-emerald-800 px-2 py-1 rounded-full shadow-sm">
            {t.condition}
          </span>
        </div>
      ) : null}

      {/* Centre — play indicator (only when video present) */}
      {hasVideo ? (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity opacity-0 group-hover:opacity-100">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/85 text-emerald-800 shadow-lg backdrop-blur-sm">
            <Play className="w-6 h-6 translate-x-0.5" fill="currentColor" />
          </span>
        </div>
      ) : null}

      {/* Bottom — name + result */}
      <div className="absolute left-0 right-0 bottom-0 p-4 text-white">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-90">
          {t.name}
          {t.age ? ` · ${t.age}` : ""}
        </div>
        <div className="text-base font-medium leading-tight mt-1 line-clamp-2">
          {t.quote ? `"${t.quote}"` : (t.story_short || t.result)}
        </div>
        <div className="text-xs opacity-80 mt-2 flex items-center gap-1">
          {t.timeline || t.result?.split("·")[0]?.trim() || "Story"}
          <ArrowRight className="w-3 h-3" />
        </div>
      </div>
    </button>
  );
}

function PlaceholderTile({ initial, tone }) {
  const cls =
    tone === "amber"
      ? "from-amber-100/80 to-amber-50 text-amber-700"
      : "from-emerald-100/80 to-emerald-50 text-emerald-700";
  return (
    <div className={`absolute inset-0 bg-gradient-to-br ${cls} flex items-center justify-center`}>
      <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/80 backdrop-blur-sm shadow-sm">
        <User className="w-7 h-7 opacity-70" />
      </span>
      <span className="sr-only">Story placeholder for {initial}</span>
    </div>
  );
}

/* ------------------------------ Fullscreen Modal ------------------------------ */

function ReelModalFullscreen({ data, onClose }) {
  const t = data;
  const vidRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);

  // Lock the page-body scroll while the modal is open. Restore on unmount.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Start playing on mount
  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = false;
    v.currentTime = 0;
    v.play().catch(() => {
      // Mobile may block — try muted autoplay
      v.muted = true;
      setMuted(true);
      v.play().catch(() => setPlaying(false));
    });
  }, []);

  const togglePlay = () => {
    const v = vidRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Wellness journey — ${t.name}`}
      data-testid="reel-modal"
      className="fixed inset-0 z-[80] bg-black/95 text-white"
      onClick={(e) => {
        // Click on backdrop (not inner) closes
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Close button — top-right with safe-area inset */}
      <button
        type="button"
        onClick={onClose}
        className="fixed top-3 right-3 z-[81] inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white"
        style={{ top: "max(0.75rem, env(safe-area-inset-top))" }}
        aria-label="Close"
        data-testid="reel-modal-close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Scrollable inner container — full svh on mobile, dialog-card on desktop.
          Use overscroll-contain so the scroll stays trapped inside the modal. */}
      <div
        className="w-full h-full overflow-y-auto overscroll-contain"
        data-testid="reel-modal-scroll"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div className="md:flex md:items-center md:justify-center md:min-h-screen md:p-8">
          <div
            className="md:max-w-4xl md:w-full md:grid md:grid-cols-2 md:bg-white md:text-emerald-950 md:rounded-3xl md:overflow-hidden md:shadow-2xl"
            data-testid="reel-modal-content"
          >
            {/* Video / poster */}
            <div className="relative bg-black md:aspect-auto md:min-h-[480px]" style={{ aspectRatio: "9/16" }}>
              {t.video_url ? (
                <video
                  ref={vidRef}
                  src={t.video_url}
                  poster={t.video_poster_url || undefined}
                  controls={false}
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                  onEnded={() => setPlaying(false)}
                />
              ) : t.video_poster_url || t.after_image_url ? (
                <img
                  src={t.video_poster_url || t.after_image_url}
                  alt={t.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <PlaceholderTile initial={t.name?.[0] || "?"} tone="emerald" />
              )}

              {t.video_url ? (
                <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 z-10">
                  <button
                    onClick={togglePlay}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 text-emerald-800 hover:bg-white shadow"
                    aria-label={playing ? "Pause" : "Play"}
                    data-testid="reel-modal-play-toggle"
                  >
                    {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 translate-x-0.5" />}
                  </button>
                  <button
                    onClick={toggleMute}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 text-emerald-800 hover:bg-white shadow"
                    aria-label={muted ? "Unmute" : "Mute"}
                    data-testid="reel-modal-mute-toggle"
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>
              ) : null}
            </div>

            {/* Text content — scrolls within the modal naturally */}
            <div className="bg-white text-emerald-950 p-6 sm:p-8 md:max-h-[80vh] md:overflow-y-auto">
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                {t.condition || "Real journey"}
              </div>
              <h3 className="font-display text-xl sm:text-2xl mt-1 leading-tight">
                {t.name}
                {t.age ? <span className="text-emerald-900/50 font-light">, {t.age}</span> : null}
              </h3>
              {t.location ? (
                <div className="text-xs text-emerald-900/60 mt-0.5">{t.location}</div>
              ) : null}

              {t.quote ? (
                <p className="mt-4 text-[15px] leading-relaxed italic border-l-2 border-amber-300 pl-3">
                  "{t.quote}"
                </p>
              ) : null}

              <div className="space-y-4 mt-6 text-sm leading-relaxed">
                <Block label="The challenge">{t.problem}</Block>
                <Block label="The plan">{t.solution}</Block>
                <Block label="The result" highlight>
                  {t.result}
                </Block>
              </div>

              {t.timeline ? (
                <div className="mt-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold">
                  {t.timeline}
                </div>
              ) : null}

              {t.before_image_url || t.after_image_url ? (
                <div className="grid grid-cols-2 gap-2 mt-6">
                  <BeforeAfterMini label="Before" url={t.before_image_url} />
                  <BeforeAfterMini label="After" url={t.after_image_url} />
                </div>
              ) : null}

              {Array.isArray(t.products) && t.products.length > 0 ? (
                <ProductPairing
                  story={t}
                  products={t.products}
                  ctaText={t.cta_text || "Explore this pairing"}
                />
              ) : null}

              <p className="mt-6 text-[11px] text-emerald-900/55 leading-relaxed">
                Wellness, not medical advice. Real stories from members; results vary.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Block({ label, children, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-1">
        {label}
      </div>
      <p className={highlight ? "font-medium text-emerald-950" : "text-emerald-950/90"}>
        {children}
      </p>
    </div>
  );
}

function BeforeAfterMini({ label, url }) {
  return (
    <div className="aspect-[4/3] relative rounded-lg overflow-hidden border border-emerald-100">
      {url ? (
        <img src={url} alt={label} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-emerald-50 flex items-center justify-center">
          <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-700/60 font-semibold">
            {label}
          </span>
        </div>
      )}
      <div className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-[0.18em] font-semibold bg-white/90 text-emerald-800 px-1.5 py-0.5 rounded-full shadow-sm">
        {label}
      </div>
    </div>
  );
}

/* ------------------------------ Product pairing ------------------------------ */

function ProductPairing({ story, products, ctaText }) {
  // Wellness-first, non-pushy section. Cards are tappable and lead to each
  // product's existing purchase_link (Stripe / direct). The closing CTA leads
  // to /register so cold visitors can take the assessment first.
  return (
    <section
      className="mt-7 pt-6 border-t border-emerald-100"
      data-testid="reel-modal-products"
      aria-label="Products used in this journey"
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
        <Sparkles className="w-3.5 h-3.5" />
        Suggested Feel Great pairing
      </div>
      <h4 className="font-display text-base sm:text-lg text-emerald-950 mt-1 leading-snug">
        Products used in this journey
      </h4>

      <ul className="mt-3 space-y-2.5">
        {products.map((p, idx) => (
          <li key={p.id}>
            <PairedProductCard
              story={story}
              product={p}
              position={idx}
              ctaText={ctaText}
            />
          </li>
        ))}
      </ul>

      <Link
        to="/register"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:text-emerald-900 group"
        data-testid="reel-modal-products-cta"
        onClick={() =>
          track.reelProductClicked(story, { id: null, name: "(closing CTA)" }, -1, ctaText)
        }
      >
        {ctaText}
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </Link>
      <p className="text-[11px] text-emerald-900/55 mt-1 leading-relaxed">
        Take the 3-minute assessment to see if this pairing fits your goals.
      </p>
    </section>
  );
}

function PairedProductCard({ story, product, position, ctaText }) {
  const href = product.purchase_link || "/register";
  const isExternal = /^https?:\/\//i.test(href);
  const price =
    typeof product.price_gbp === "number" && product.price_gbp > 0
      ? `£${product.price_gbp.toFixed(2)}`
      : product.price || "";

  const onClick = () => {
    track.reelProductClicked(story, product, position, ctaText);
  };

  const inner = (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-emerald-100 bg-white hover:border-emerald-200 hover:shadow-sm transition-all group">
      <div className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-emerald-50 border border-emerald-100">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-[0.18em] text-emerald-700/60 font-semibold">
            FG
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-emerald-950 leading-tight">{product.name}</span>
          {price ? (
            <span className="text-[11px] uppercase tracking-[0.16em] text-emerald-700/70 font-semibold">
              {price}
            </span>
          ) : null}
        </div>
        {product.reason ? (
          <p className="text-xs text-emerald-900/70 mt-0.5 leading-relaxed line-clamp-2">
            {product.reason}
          </p>
        ) : null}
      </div>
      <ArrowRight className="w-4 h-4 text-emerald-700 shrink-0 transition-transform group-hover:translate-x-0.5" />
    </div>
  );

  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        data-testid={`reel-modal-product-${product.id}`}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 rounded-xl"
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={href || "/register"}
      onClick={onClick}
      data-testid={`reel-modal-product-${product.id}`}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 rounded-xl"
    >
      {inner}
    </Link>
  );
}
